'use client';

import * as React from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from './cn';
import { useId } from './hooks';

export interface FieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label?: React.ReactNode;
  /** Helper text under the control. Hidden while `error` is shown. */
  hint?: React.ReactNode;
  /** Error message; also flips the control to the danger style via `aria-invalid`. */
  error?: React.ReactNode;
  /** Adds a discreet "(רשות)" style marker instead of the usual asterisk. */
  optionalLabel?: React.ReactNode;
  required?: boolean;
  /** Render prop receives the ids to wire onto the control. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => React.ReactNode;
}

/**
 * Label + control + hint/error, wired with `aria-describedby` / `aria-invalid`.
 * Input, Textarea, Select and ColorPicker all use it, so every field in the app
 * announces the same way.
 */
export function Field({
  label,
  hint,
  error,
  optionalLabel,
  required,
  className,
  children,
  ...props
}: FieldProps) {
  const id = useId('field');
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);
  const describedBy = invalid ? errorId : hint ? hintId : undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)} {...props}>
      {label ? (
        <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-ink">
          {label}
          {required ? (
            <span className="text-accent" aria-hidden>
              *
            </span>
          ) : optionalLabel ? (
            <span className="text-2xs font-normal text-ink-3">{optionalLabel}</span>
          ) : null}
        </label>
      ) : null}

      {children({ id, describedBy, invalid })}

      {invalid ? (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-xs text-danger">
          <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Shared control chrome: border, radius, focus ring, invalid + disabled states. */
export const CONTROL_BASE = cn(
  'w-full rounded-md border border-line bg-surface-2 text-ink',
  'placeholder:text-ink-3',
  'transition-[border-color,box-shadow,background-color] duration-(--duration-fast) ease-out-soft',
  'hover:border-line-strong',
  'focus-visible:border-accent focus-visible:outline-none',
  'focus-visible:shadow-[0_0_0_3px_var(--color-accent-soft-2)]',
  'aria-invalid:border-danger aria-invalid:focus-visible:shadow-[0_0_0_3px_var(--color-danger-soft)]',
  'disabled:cursor-not-allowed disabled:bg-paper-2 disabled:text-ink-3',
);

/** 16 px on phones (iOS refuses to zoom), 14 px from `sm` up. */
export const CONTROL_TEXT = 'text-base sm:text-sm';

export const CONTROL_SIZE = {
  sm: 'h-9 px-3',
  md: 'h-touch px-3.5',
  lg: 'h-13 px-4',
} as const;

export type ControlSize = keyof typeof CONTROL_SIZE;
